import { Link, createFileRoute } from '@tanstack/react-router'
import { FileTextIcon, PlusIcon } from 'lucide-react'
import type { ArticleSummary } from '@hedgehog/shared'
import { ApiRequestError } from '@/lib/api'
import { OrderError, TabGate } from '@/components/app/order'
import { LoadingState } from '@/components/app/states'
import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { articleStatus, articlesApi, fmtDate } from '@/components/articles/api'
import { cn } from '@/lib/utils'

// Articles: the site's blog posts, drafts and published, with "New article". The editor is
// o.$orderId.articles.$postId.tsx; both call the site's content API through the Hedgehog API.

type Loaded = { items: ArticleSummary[]; unavailable: null } | { items: null; unavailable: string }

export const Route = createFileRoute('/o/$orderId/articles/')({
  loader: async ({ params, abortController }): Promise<Loaded> => {
    try {
      return { items: (await articlesApi(params.orderId).list(abortController.signal)).items, unavailable: null }
    } catch (e) {
      // not_ready: the site has no editor secret yet. 404: the site runs a release without articles.
      if (e instanceof ApiRequestError && (e.code === 'not_ready' || e.status === 404)) {
        return { items: null, unavailable: e.code === 'not_ready' ? e.message : 'Articles are not available on your site yet. We will email you when they are.' }
      }
      throw e
    }
  },
  pendingComponent: () => <LoadingState label="Loading articles" />,
  errorComponent: ({ error }) => (
    <TabGate tab="/o/$orderId/articles">
      <OrderError error={error} title="We could not load your articles" />
    </TabGate>
  ),
  component: () => (
    <TabGate tab="/o/$orderId/articles">
      <Articles />
    </TabGate>
  ),
})

const TONE: Record<ReturnType<typeof articleStatus>['tone'], string> = {
  draft: 'bg-muted text-muted-foreground',
  live: 'bg-success/10 text-success',
  changes: 'bg-warning/10 text-warning',
  scheduled: 'bg-secondary text-secondary-foreground',
}

function Articles() {
  const data = Route.useLoaderData()
  const { orderId } = Route.useParams()
  const newButton = (
    <Link to="/o/$orderId/articles/$postId" params={{ orderId, postId: 'new' }} className={buttonVariants()}>
      <PlusIcon aria-hidden />
      New article
    </Link>
  )

  return (
    <section aria-labelledby="articles" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id="articles" className="text-xl font-semibold">
            Articles
          </h2>
          <p className="text-sm text-muted-foreground">Write news, tips and stories for your blog. Drafts stay private until you publish them.</p>
        </div>
        {data.items && newButton}
      </div>

      {data.unavailable !== null ? (
        <p className="rounded-xl border border-border p-4 text-sm">{data.unavailable}</p>
      ) : data.items.length === 0 ? (
        <Empty className="border border-dashed border-border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileTextIcon />
            </EmptyMedia>
            <EmptyTitle>No articles yet</EmptyTitle>
            <EmptyDescription>Your first article shows on your blog page once you publish it.</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>{newButton}</EmptyContent>
        </Empty>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-xl border border-border">
          {data.items.map((a) => {
            const s = articleStatus(a)
            const date = a.status === 'published' && a.publishedAt ? `Published ${fmtDate(a.publishedAt)}` : a.updatedAt ? `Edited ${fmtDate(a.updatedAt)}` : null
            return (
              <li key={a.id}>
                <Link
                  to="/o/$orderId/articles/$postId"
                  params={{ orderId, postId: a.id }}
                  className="flex flex-col gap-1 px-4 py-3 outline-none hover:bg-muted/50 focus-visible:ring-3 focus-visible:ring-ring/50 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium">{a.title || 'Untitled'}</span>
                    {a.excerpt && <span className="truncate text-sm text-muted-foreground">{a.excerpt}</span>}
                  </span>
                  <span className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
                    {date && <span>{date}</span>}
                    <Badge className={cn('h-6 px-2', TONE[s.tone])}>{s.label}</Badge>
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
